import axios, {AxiosInstance, AxiosRequestConfig} from "axios";
import Utils from "../Utils";
import {refreshAccessToken} from "../actions/authActions";
import AuthUtils from "../AuthUtils";
import {getUserFunctionalities} from "../actions/boCoreActions";
import {Routes} from "./routes";
import store from "./store";
import {actions} from "../reducers/auth";

export default class Axios {
    // @ts-ignore
    static API_BASE_URL: string = API_BASE_URL;
    static cancelToken = axios.CancelToken.source();
    static cancelTokenModals = axios.CancelToken.source();
    static cancelTokenTimeOut: any = 0;
    static cancelTokenTimeOutModals: any = 0;
    static readonly instance: AxiosInstance = Axios.create({
        baseURL: `${Axios.API_BASE_URL}/`,
        cancelToken: Axios.cancelToken.token
    });

    // refresh token related
    private static isAlreadyFetchingAccessToken = false;
    private static subscribers = [];

    static addV2toBaseUrl = (url: string) => {
        if (url.endsWith(Utils.API_BASE_URL_V1_POSTFIX)) {
            url = url.substring(0, url.length - Utils.API_BASE_URL_V1_POSTFIX.length);
            url = url + Utils.API_BASE_URL_V2_POSTFIX;
            return url;
        }
    };

    static removeURLVersion = (url: string) => {
        if (url.endsWith(Utils.API_BASE_URL_V1_POSTFIX)) {
            url = url.substring(0, url.length - Utils.API_BASE_URL_V1_POSTFIX.length);
            url = url + '/';
            return url;
        }
        if (url.endsWith(Utils.API_BASE_URL_V2_POSTFIX)) {
            url = url.substring(0, url.length - Utils.API_BASE_URL_V2_POSTFIX.length);
            url = url + '/';
            return url;
        }
    }


    static readonly instanceV2: AxiosInstance = Axios.create({
        baseURL: `${Axios.addV2toBaseUrl(Axios.API_BASE_URL)}/`,
        cancelToken: Axios.cancelToken.token
    });

    static readonly graphqlInstance: AxiosInstance = Axios.create({
        baseURL: `${Axios.removeURLVersion(Axios.API_BASE_URL)}/`,
        withCredentials: true,
        headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Time-Zone': Utils.getCookie("Time-Zone")
        },
        cancelToken: Axios.cancelToken.token
    });

    // This is instance helps you when you want to close a modal while there are running requests.
    // To stop the requests use cancelTokenModals.
    static readonly instanceForModals: AxiosInstance = Axios.createModalInstance({
        baseURL: `${Axios.API_BASE_URL}/`,
        cancelToken: Axios.cancelTokenModals.token
    });

    static create(config: AxiosRequestConfig): AxiosInstance {
        const instance = axios.create(config);
        instance.interceptors.request.use(
            config => this.appendAuthorizationBearerToken(config),
            error => Promise.reject(error)
        );
        instance.interceptors.response.use(
            response => response,
            async error => {
                const {config, response: {status}} = error;
                const originalRequest = config;
                if (status === 403) {
                    if (AuthUtils.user) {
                        getUserFunctionalities()
                            .then((res) => {
                                let uiRoutesArr = [];
                                res.data.data.forEach(functionality => {
                                    uiRoutesArr = uiRoutesArr.concat(functionality.uiRoutes);
                                })
                                if (AuthUtils.user.mta || uiRoutesArr.includes(Routes.ALL_ROUTES) || AuthUtils.user.systemAdministrator) {
                                    let uiRoutes =  Object.keys(Routes).map(route => {
                                        return Routes[route]
                                    });
                                    if (!AuthUtils.user.mta) {
                                        uiRoutes = uiRoutes.filter(route => route !== Routes.TENANT_MANAGEMENT &&
                                        route !== Routes.DATA_VIEW_CONFIG && route !== Routes.BUSINESS_OBJECTS)
                                    }
                                    store.dispatch({type: actions.SET_UI_ROUTES, payload: Array.from(uiRoutes)});
                                } else {
                                    const uniqueRoutes = new Set(uiRoutesArr);
                                    store.dispatch({type: actions.SET_UI_ROUTES, payload: Array.from(uniqueRoutes)});
                                }
                            })
                            .catch(() => {
                                store.dispatch({type: actions.SET_UI_ROUTES, payload: []});
                            })
                    }
                }
                if (status === 401 && AuthUtils.isRefreshTokenPresent()) {
                    if (!this.isAlreadyFetchingAccessToken) {
                        this.isAlreadyFetchingAccessToken = true;
                        refreshAccessToken()
                            .then((response) => {
                                AuthUtils.setSessionStorageTokens(response.data.access_token, response.data.refresh_token);
                                this.isAlreadyFetchingAccessToken = false;
                                this.onAccessTokenFetched(response.data.access_token);
                            }).catch(() => {
                                AuthUtils.clearSessionStorageTokens();
                                window.location.href = `${process.env.ENV_PREFIX}/login`;
                            });
                    }

                    const retryOriginalRequest = new Promise((resolve) => {
                        this.addSubscriber(access_token => {
                            originalRequest.headers.Authorization = 'Bearer ' + access_token;
                            originalRequest.baseURL = '';
                            resolve(axios(originalRequest))
                        })
                    });
                    return retryOriginalRequest
                } else if (error.request.responseType === 'blob' && error.response.data instanceof Blob &&
                    error.response.data.type && error.response.data.type.toLowerCase().indexOf('json') !== -1) {
                    return new Promise((resolve, reject) => {
                        let reader = new FileReader();
                        reader.onload = () => {
                            if (typeof reader.result === "string") {
                                error.response.data = JSON.parse(reader.result);
                            }
                            resolve(Promise.reject(error));
                        };

                        reader.onerror = () => {
                            reject(error);
                        };

                        reader.readAsText(error.response.data);
                    });
                } else {
                    return Promise.reject(error);
                }
            }
        );
        return instance;
    }

    static appendAuthorizationBearerToken = (config: any) => {
        const withCredentials = config.withCredentials ?? true;
        if (!withCredentials) {
            return config;
        }
        config.headers.Authorization = `Bearer ${AuthUtils.getAccessToken()}`;
        return config;
    };

    static onAccessTokenFetched = (access_token: string) => {
        Axios.subscribers = Axios.subscribers.filter(callback => callback(access_token))
    };

    static addSubscriber = (callback) => {
        Axios.subscribers.push(callback)
    };

    static createModalInstance(config: AxiosRequestConfig): AxiosInstance {
        const instance = axios.create(config);
        instance.interceptors.request.use(
            config => this.appendAuthorizationBearerToken(config),
            error => Promise.reject(error)
        );
        instance.interceptors.response.use(
            response => response,
            async error => {
                if (error instanceof axios.Cancel && Axios.cancelTokenModals.token.reason && Axios.cancelTokenModals.token.reason.message === "User logout") {
                    return await new Promise(resolve => {
                        clearTimeout(Axios.cancelTokenTimeOutModals);
                        Axios.cancelTokenTimeOutModals = setTimeout(() => {
                            window.location.reload();
                        }, 10000)
                    })
                } else if (error.response && error.response.status === 401 && !error.config.url.endsWith("login") && !error.config.url.endsWith("myself")) {
                    window.location.reload();
                } else {
                    return Promise.reject(error);
                }
            }
        );
        return instance;
    }

    static resetCancelToken = () => {
        clearTimeout(Axios.cancelTokenTimeOut);
        Axios.cancelToken = axios.CancelToken.source();
        Axios.instance.defaults.cancelToken = Axios.cancelToken.token;
    };

    static resetCancelTokenForModals = () => {
        clearTimeout(Axios.cancelTokenTimeOutModals);
        Axios.cancelTokenModals = axios.CancelToken.source();
        Axios.instanceForModals.defaults.cancelToken = Axios.cancelTokenModals.token;
    };

    static async all<T>(values: (T | Promise<T>)[]): Promise<T[]> {
        return await axios.all(values);
    }

    static isCancel(value: any): boolean {
        return axios.isCancel(value)
    }

}



// WEBPACK FOOTER //
// ./app/config/Axios.ts